import React from "react";
import ReactMarkdown from "react-markdown";
import {
  Loader,
  MapPin,
  BarChart3,
  AlertTriangle,
  CloudRainWind,
  Leaf,
  CheckCircle2,
  ShieldAlert,
  Activity,
  Sprout,
} from "lucide-react";

export interface AIInterpretationData {
  title?: string; // Optional title
  risk_level?: string; // e.g., LOW, MODERATE, HIGH, SEVERE
  impact_level?: string; // e.g., NEGATIVE, NEUTRAL, POSITIVE
  risks?: string[]; // List of identified risks/stressors
  impact?: string; // Narrative about yield/general impact
  adaptation?: string[]; // List of adaptation strategies
}

interface AIInterpretationDisplayProps {
  interpretation?: string;
  data?: AIInterpretationData | null;
  isLoading?: boolean;
  error?: string | null;
  className?: string;
}

/**
 * Reusable component to display formatted AI interpretations
 * Supports both Markdown string and structured JSON data
 */
const AIInterpretationDisplay: React.FC<AIInterpretationDisplayProps> = ({
  interpretation,
  data,
  isLoading = false,
  error = null,
  className = "",
}) => {
  const extractText = (children: React.ReactNode): string => {
    if (typeof children === "string") return children;
    if (Array.isArray(children)) return children.map(extractText).join("");
    if (React.isValidElement(children))
      return extractText(children.props.children);
    return "";
  };

  const sectionMeta = (title: string) => {
    const t = (title || "").toLowerCase();

    // Mapping for new simplified sections
    if (t.includes("risks") || t.includes("risk")) {
      return { color: "#ef4444", Icon: ShieldAlert };
    }
    if (t.includes("impact")) {
      return { color: "#f59e0b", Icon: Activity };
    }
    if (t.includes("adaptation")) {
      return { color: "#22c55e", Icon: Sprout };
    }

    // Fallback/Legacy mappings
    if (
      t.includes("📍") ||
      t.includes("general overview") ||
      t.includes("summary")
    ) {
      return { color: "#14b8a6", Icon: MapPin };
    }
    if (
      t.includes("🌽") ||
      t.includes("expected impact") ||
      t.includes("trends")
    ) {
      return { color: "#f59e0b", Icon: BarChart3 };
    }
    if (t.includes("⚠️") || t.includes("identified climate risks")) {
      return { color: "#ef4444", Icon: AlertTriangle };
    }
    if (t.includes("🌦️") || t.includes("dominant climate stressors")) {
      return { color: "#4f46e5", Icon: CloudRainWind };
    }
    if (
      t.includes("🌱") ||
      t.includes("recommended adaptation") ||
      t.includes("recommendations")
    ) {
      return { color: "#22c55e", Icon: Leaf };
    }
    if (
      t.includes("✅") ||
      t.includes("key takeaway") ||
      t.includes("confidence")
    ) {
      return { color: "#3b82f6", Icon: CheckCircle2 };
    }
    return { color: "#3b82f6", Icon: CheckCircle2 };
  };

  if (error) {
    return (
      <div
        className={`rounded-lg bg-red-50 p-4 border border-red-200 ${className}`}
      >
        <div className="flex items-center text-red-700">
          <span className="font-medium">AI Analysis Error:</span>
          <span className="ml-2">{error}</span>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        className={`flex flex-row items-center justify-center py-4 bg-blue-50 rounded-lg border border-blue-200 ${className}`}
      >
        <Loader className="animate-spin text-blue-600 mr-2" size={20} />
        <span className="text-blue-700 font-medium">
          AI Analysis in progress...
        </span>
      </div>
    );
  }

  // Render Structured Data if available
  if (data) {
    // Helper for rendering section header style (matching Markdown fallback)
    const renderHeader = (
      title: string,
      color: string,
      Icon: React.ElementType,
      badge?: string,
    ) => (
      <div
        className="mb-3 rounded-lg"
        style={{
          borderLeft: `4px solid ${color}`,
          background: `${color}26`, // 15% opacity hex
          padding: "0.5rem 0.75rem",
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon style={{ color }} width={20} height={20} />
            <h2 className="text-lg font-semibold" style={{ color, margin: 0 }}>
              {title}
            </h2>
          </div>
          {badge && (
            <span
              className="text-xs font-bold px-2 py-1 rounded-full uppercase tracking-wider"
              style={{ backgroundColor: color, color: "#fff" }}
            >
              {badge} Risk
            </span>
          )}
        </div>
      </div>
    );

    // Normalize fields: the model may return objects/arrays instead of the
    // expected strings/arrays (causes ".trim is not a function").
    const rawImpact: any = data.impact;
    const impactText =
      typeof rawImpact === "string"
        ? rawImpact
        : Array.isArray(rawImpact)
          ? rawImpact.map((s: any) => String(s)).join("\n")
          : rawImpact != null
            ? String(rawImpact)
            : "";
    const riskList = Array.isArray(data.risks)
      ? data.risks.map((r) => String(r))
      : data.risks
        ? [String(data.risks)]
        : [];
    const adaptationList = Array.isArray(data.adaptation)
      ? data.adaptation.map((a) => String(a))
      : data.adaptation
        ? [String(data.adaptation)]
        : [];

    const risksMeta = sectionMeta("Risks");
    const impactMeta = sectionMeta("Impact");
    const adaptationMeta = sectionMeta("Adaptation");

    // Check if there is meaningful impact data
    const hasMeaningfulImpact =
      impactText.trim().length > 0 &&
      !impactText.toLowerCase().includes("not available") &&
      !impactText.toLowerCase().includes("no specific impact") &&
      !impactText.toLowerCase().includes("no clear impact");

    return (
      <div
        className={`rounded-lg p-4 ${className}`}
        style={{ background: "#ffffff", border: "1px solid #e5e7eb" }}
      >
        {/* Risks Section */}
        {riskList.length > 0 && (
          <section className="mb-6">
            {renderHeader(
              "Risks",
              risksMeta.color,
              risksMeta.Icon,
              data.risk_level,
            )}
            <div
              className="p-3 rounded-md"
              style={{ background: `${risksMeta.color}15` }} // 8% opacity hex
            >
              <ol
                className="space-y-0.5 list-decimal pl-5"
                style={{ color: "#374151" }}
              >
                {riskList.map((risk, idx) => (
                  <li key={idx} className="list-item leading-snug">
                    {risk}
                  </li>
                ))}
              </ol>
            </div>
          </section>
        )}

        {/* Impact Section */}
        {hasMeaningfulImpact && (
          <section className="mb-6">
            {renderHeader(
              "Impact",
              impactMeta.color,
              impactMeta.Icon,
              data.impact_level,
            )}
            <div
              className="p-3 rounded-md"
              style={{ background: `${impactMeta.color}15` }} // 8% opacity hex
            >
              <p
                className="mb-0 leading-snug whitespace-pre-line"
                style={{ color: "#374151" }}
              >
                {impactText}
              </p>
            </div>
          </section>
        )}

        {/* Adaptation Section */}
        <section className="mb-2">
          {renderHeader(
            "Adaptation",
            adaptationMeta.color,
            adaptationMeta.Icon,
          )}
          <div
            className="p-3 rounded-md"
            style={{ background: `${adaptationMeta.color}15` }} // 8% opacity hex
          >
            {!hasMeaningfulImpact ? (
              <p className="mb-0 leading-snug text-gray-600 italic">
                Insufficient impact data is available for this specific
                selection to formulate precise adaptation strategies. Please
                adjust your filters or consult local agricultural extension
                services for general guidance.
              </p>
            ) : adaptationList.length > 0 ? (
              <ol
                className="space-y-0.5 list-decimal pl-5"
                style={{ color: "#374151" }}
              >
                {adaptationList.map((rec, idx) => (
                  <li key={idx} className="list-item leading-snug">
                    {rec}
                  </li>
                ))}
              </ol>
            ) : (
              <p className="mb-0 leading-snug text-gray-600 italic">
                No specific adaptation strategies are available in the dataset
                for this selection.
              </p>
            )}
          </div>
        </section>
      </div>
    );
  }

  // Fallback to Markdown rendering
  const rawText =
    interpretation ||
    "*Analysis in progress... Please wait while AI processes the climate data.*";

  // Extract risk level if present in title (e.g. "## Risks (HIGH Risk)")
  const extractRiskBadge = (title: string) => {
    const match = title.match(/\((LOW|MODERATE|HIGH|SEVERE)\s*Risk\)/i);
    return match ? match[1].toUpperCase() : null;
  };

  // Extract impact level if present in title (e.g. "## Impact (NEGATIVE Impact)")
  const extractImpactBadge = (title: string) => {
    const match = title.match(
      /\((NEGATIVE|NEUTRAL|POSITIVE|UNCERTAIN|SLIGHTLY NEGATIVE)\s*Impact\)/i,
    );
    return match ? match[1].toUpperCase() : null;
  };

  // Clean title for display
  const cleanTitle = (title: string) => {
    return title
      .replace(/\s*\((LOW|MODERATE|HIGH|SEVERE)\s*Risk\)/i, "")
      .replace(
        /\s*\((NEGATIVE|NEUTRAL|POSITIVE|UNCERTAIN|SLIGHTLY NEGATIVE)\s*Impact\)/i,
        "",
      )
      .trim();
  };

  // Check if the text seems to follow the "## Title" structure
  const hasSections = rawText.includes("##");

  if (hasSections) {
    const parts = rawText.split(/##\s+/).filter((s) => s.trim().length > 0);

    // Check for meaningful impact in raw text
    const impactPart = parts.find((p) => p.toLowerCase().startsWith("impact"));
    const hasMeaningfulImpactRaw =
      impactPart &&
      !impactPart.toLowerCase().includes("not available") &&
      !impactPart.toLowerCase().includes("no specific impact") &&
      !impactPart.toLowerCase().includes("no clear impact") &&
      !impactPart
        .toLowerCase()
        .includes("does not provide a reliable estimate");

    return (
      <div
        className={`rounded-lg p-4 ${className}`}
        style={{ background: "#ffffff", border: "1px solid #e5e7eb" }}
      >
        {parts.map((part, idx) => {
          const newlineIdx = part.indexOf("\n");
          let rawTitle = "";
          let content = part;

          if (newlineIdx !== -1) {
            rawTitle = part.substring(0, newlineIdx).trim();
            content = part.substring(newlineIdx).trim();
          } else {
            rawTitle = part.trim();
            content = "";
          }

          const isImpact = rawTitle.toLowerCase().includes("impact");
          const isAdaptation = rawTitle.toLowerCase().includes("adaptation");

          // Skip rendering Impact section if it's not meaningful
          if (isImpact && !hasMeaningfulImpactRaw) {
            return null;
          }

          let badge = extractRiskBadge(rawTitle);
          if (!badge && isImpact) {
            badge = extractImpactBadge(rawTitle);
          }

          const title = cleanTitle(rawTitle);
          const { color, Icon } = sectionMeta(title);

          return (
            <section key={idx} className="mb-6 last:mb-0">
              {/* Header */}
              <div
                className="mb-3 rounded-lg"
                style={{
                  borderLeft: `4px solid ${color}`,
                  background: `${color}26`,
                  padding: "0.5rem 0.75rem",
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon style={{ color }} width={20} height={20} />
                    <h2
                      className="text-lg font-semibold"
                      style={{ color, margin: 0 }}
                    >
                      {title}
                    </h2>
                  </div>
                  {badge && (
                    <span
                      className="text-xs font-bold px-2 py-1 rounded-full uppercase tracking-wider"
                      style={{ backgroundColor: color, color: "#fff" }}
                    >
                      {badge} Risk
                    </span>
                  )}
                </div>
              </div>

              {/* Content with background */}
              <div
                className="p-3 rounded-md"
                style={{ background: `${color}15` }}
              >
                <div className="prose prose-sm max-w-none">
                  {isAdaptation && !hasMeaningfulImpactRaw ? (
                    <p className="mb-0 leading-snug text-gray-600 italic">
                      Insufficient impact data is available for this specific
                      selection to formulate precise adaptation strategies.
                      Please adjust your filters or consult local agricultural
                      extension services for general guidance.
                    </p>
                  ) : isAdaptation &&
                    (!content ||
                      content.toLowerCase().includes("not available")) ? (
                    <p className="mb-0 leading-snug text-gray-600 italic">
                      No specific adaptation strategies are available in the
                      dataset for this selection.
                    </p>
                  ) : (
                    <ReactMarkdown
                      components={{
                        h3: ({ children }) => (
                          <h3
                            className="text-md font-medium mb-1 mt-2"
                            style={{ color: "#1f2937" }}
                          >
                            {children}
                          </h3>
                        ),
                        p: ({ children }) => (
                          <p
                            className="mb-1.5 leading-snug"
                            style={{ color: "#374151" }}
                          >
                            {children}
                          </p>
                        ),
                        ol: ({ children }) => (
                          <ol
                            className="mb-1.5 space-y-0.5 list-decimal pl-5"
                            style={{ color: "#374151" }}
                          >
                            {children}
                          </ol>
                        ),
                        ul: ({ children }) => (
                          <ol
                            className="mb-1.5 space-y-0.5 list-decimal pl-5"
                            style={{ color: "#374151" }}
                          >
                            {children}
                          </ol>
                        ),
                        li: ({ children }) => (
                          <li
                            className="list-item leading-snug"
                            style={{ color: "#374151" }}
                          >
                            {children}
                          </li>
                        ),
                        strong: ({ children }) => (
                          <strong
                            className="font-semibold"
                            style={{ color: "#111827" }}
                          >
                            {children}
                          </strong>
                        ),
                      }}
                    >
                      {content}
                    </ReactMarkdown>
                  )}
                </div>
              </div>
            </section>
          );
        })}
      </div>
    );
  }

  // Legacy/Simple Fallback (no sections detected)
  return (
    <div
      className={`rounded-lg p-4 ${className}`}
      style={{ background: "#ffffff", border: "1px solid #e5e7eb" }}
    >
      <div className="prose prose-sm max-w-none">
        <ReactMarkdown
          components={{
            h2: ({ children }) => {
              const title = extractText(children);
              const { color, Icon } = sectionMeta(title);
              return (
                <div
                  className="mt-3 first:mt-0 mb-1 rounded-lg"
                  style={{
                    borderLeft: `4px solid ${color}`,
                    background: `${color}26`,
                    padding: "0.5rem 0.75rem",
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Icon style={{ color }} width={20} height={20} />
                    <h2
                      className="text-lg font-semibold"
                      style={{ color, margin: 0 }}
                    >
                      {children}
                    </h2>
                  </div>
                </div>
              );
            },
            h3: ({ children }) => (
              <h3
                className="text-md font-medium mb-1 mt-2"
                style={{ color: "#1f2937" }}
              >
                {children}
              </h3>
            ),
            p: ({ children }) => (
              <p className="mb-1 leading-snug" style={{ color: "#374151" }}>
                {children}
              </p>
            ),
            ol: ({ children }) => (
              <ol
                className="mb-1 space-y-0.5 list-decimal pl-5"
                style={{ color: "#374151" }}
              >
                {children}
              </ol>
            ),
            ul: ({ children }) => (
              <ol
                className="mb-1 space-y-0.5 list-decimal pl-5"
                style={{ color: "#374151" }}
              >
                {children}
              </ol>
            ),
            li: ({ children }) => (
              <li
                className="list-item leading-snug"
                style={{ color: "#374151" }}
              >
                {children}
              </li>
            ),
            strong: ({ children }) => (
              <strong className="font-semibold" style={{ color: "#111827" }}>
                {children}
              </strong>
            ),
            hr: () => (
              <hr className="my-4" style={{ borderColor: "#e5e7eb" }} />
            ),
            blockquote: ({ children }) => (
              <blockquote
                className="border-l-4 pl-4 italic my-3"
                style={{ borderColor: "#d1d5db", color: "#374151" }}
              >
                {children}
              </blockquote>
            ),
            code: ({ children }) => (
              <code
                className="px-1 py-0.5 rounded text-sm"
                style={{ background: "#f3f4f6", color: "#1f2937" }}
              >
                {children}
              </code>
            ),
          }}
        >
          {interpretation ||
            "*Analysis in progress... Please wait while AI processes the climate data.*"}
        </ReactMarkdown>
      </div>
    </div>
  );
};

export default AIInterpretationDisplay;
